import { useEffect } from "react";

const RING_LOW_FREQUENCY_HZ = 440;
const RING_HIGH_FREQUENCY_HZ = 480;
const RING_ON_SECONDS = 2;
const RING_PERIOD_SECONDS = 6;
const RING_RAMP_SECONDS = 0.02;
const RING_VOLUME = 0.05;
const TITLE_FLASH_TEXT = "Incoming call…";
const TITLE_FLASH_INTERVAL_MILLISECONDS = 1000;

/**
 * Plays a looping ringtone (classic dual 440 + 480 Hz tone, 2s ring / 4s
 * silence) and flashes the document title while the calling component is
 * mounted. Everything is generated with the Web Audio API — no audio asset.
 *
 * If the browser's autoplay policy keeps the AudioContext suspended (no user
 * gesture yet), the ring is silent; the title still flashes.
 */
const useIncomingCallRing = () => {
  useEffect(() => {
    if (typeof window === "undefined" || !window.AudioContext) {
      return undefined;
    }

    const context = new AudioContext();
    const gain = context.createGain();
    gain.gain.value = 0;
    gain.connect(context.destination);

    const oscillators = [RING_LOW_FREQUENCY_HZ, RING_HIGH_FREQUENCY_HZ].map(
      (frequency) => {
        const oscillator = context.createOscillator();
        oscillator.type = "sine";
        oscillator.frequency.value = frequency;
        oscillator.connect(gain);
        oscillator.start();
        return oscillator;
      },
    );

    const scheduleRing = () => {
      const now = context.currentTime;
      gain.gain.cancelScheduledValues(now);
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(RING_VOLUME, now + RING_RAMP_SECONDS);
      gain.gain.setValueAtTime(RING_VOLUME, now + RING_ON_SECONDS);
      gain.gain.linearRampToValueAtTime(
        0,
        now + RING_ON_SECONDS + RING_RAMP_SECONDS,
      );
    };

    // Autoplay policy: if resume() is rejected or the context stays
    // suspended, fail silently — there is nothing more we can do without a
    // user gesture.
    context.resume().catch(() => {});
    scheduleRing();
    const interval = setInterval(scheduleRing, RING_PERIOD_SECONDS * 1000);

    return () => {
      clearInterval(interval);
      oscillators.forEach((oscillator) => oscillator.stop());
      context.close().catch(() => {});
    };
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") {
      return undefined;
    }

    let originalTitle = document.title;
    const interval = setInterval(() => {
      if (document.title === TITLE_FLASH_TEXT) {
        document.title = originalTitle;
      } else {
        // BadgeCount (or anything else) may have changed the title since the
        // last tick — keep the latest value to restore.
        originalTitle = document.title;
        document.title = TITLE_FLASH_TEXT;
      }
    }, TITLE_FLASH_INTERVAL_MILLISECONDS);

    return () => {
      clearInterval(interval);
      if (document.title === TITLE_FLASH_TEXT) {
        document.title = originalTitle;
      }
    };
  }, []);
};

export default useIncomingCallRing;
